import React, { useState, useEffect, useCallback } from 'react';
import recruitingService from '../utils/recruitingService';
import KPICard from '../components/KPICard';
import HiresByMonthChart from '../components/HiresByMonthChart';
import HiresByJobTitleChart from '../components/HiresByJobTitleChart';
import HiresBySourceChart from '../components/HiresBySourceChart';
import RecentHiresTable from '../components/RecentHiresTable';
import './HiresDashboard.css';

/**
 * Hires Dashboard
 * Analytics view focused on completed hires and hiring trends
 */
function HiresDashboard() {
    const [summary, setSummary] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [filters, setFilters] = useState({
        startDate: '',
        endDate: '',
        recruiter: '',
        department: ''
    });
    const [filterOptions, setFilterOptions] = useState({
        recruiters: [],
        departments: []
    });

    // Fetch filter options
    useEffect(() => {
        const fetchFilterOptions = async () => {
            try {
                const response = await recruitingService.getFilterOptions();
                if (response.success) {
                    setFilterOptions({
                        recruiters: response.data.recruiters || [],
                        departments: response.data.departments || []
                    });
                }
            } catch (err) {
                console.error('Error fetching filter options:', err);
            }
        };
        fetchFilterOptions();
    }, []);

    // Fetch summary data
    const fetchSummary = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await recruitingService.getHiresSummary(filters);
            if (response.success) {
                setSummary(response.data);
            } else {
                setError('Failed to load hires summary');
            }
        } catch (err) {
            console.error('Error fetching hires summary:', err);
            setError('Failed to load hires summary');
        }
        setLoading(false);
    }, [filters]);

    useEffect(() => {
        fetchSummary();
    }, [fetchSummary]);

    // Handle filter changes
    const handleFilterChange = (field, value) => {
        setFilters(prev => ({
            ...prev,
            [field]: value
        }));
    };

    // Get current year for display
    const currentYear = new Date().getFullYear();

    return (
        <div className="hires-dashboard">
            {/* Dashboard Header */}
            <div className="dashboard-header">
                <div className="header-title-section">
                    <h1 className="dashboard-title">Hires Dashboard</h1>
                    <p className="dashboard-subtitle">Hiring outcomes and trends analysis - YTD {currentYear}</p>
                </div>
                <div className="header-filters">
                    <select
                        className="filter-select"
                        value={filters.recruiter}
                        onChange={(e) => handleFilterChange('recruiter', e.target.value)}
                    >
                        <option value="">All Recruiters</option>
                        {filterOptions.recruiters.map(r => (
                            <option key={r} value={r}>{r}</option>
                        ))}
                    </select>
                    <select
                        className="filter-select"
                        value={filters.department}
                        onChange={(e) => handleFilterChange('department', e.target.value)}
                    >
                        <option value="">All Departments</option>
                        {filterOptions.departments.map(d => (
                            <option key={d} value={d}>{d}</option>
                        ))}
                    </select>
                </div>
            </div>

            {/* Error State */}
            {error && (
                <div className="dashboard-error">
                    <p>{error}</p>
                    <button onClick={fetchSummary}>Retry</button>
                </div>
            )}

            {/* KPI Cards */}
            <div className="kpi-grid">
                <KPICard
                    title="Total Hires YTD"
                    value={summary?.totalHires ?? '-'}
                    subtitle="Completed onboarding"
                    loading={loading}
                    color="success"
                />
                <KPICard
                    title="Avg Time to Hire"
                    value={summary?.avgTimeToHire ?? '-'}
                    suffix=" days"
                    subtitle="From job assigned to start"
                    loading={loading}
                    color="primary"
                />
                <KPICard
                    title="Avg Time to Offer"
                    value={summary?.avgTimeToOffer ?? '-'}
                    suffix=" days"
                    subtitle="From candidate created to offer"
                    loading={loading}
                    color="info"
                />
                <KPICard
                    title="In Offer Stage"
                    value={summary?.candidatesInOffer ?? '-'}
                    subtitle="Pending acceptance"
                    loading={loading}
                    color="warning"
                />
                <KPICard
                    title="Onboarding Rate"
                    value={summary?.onboardingCompletionRate ?? '-'}
                    suffix="%"
                    subtitle="Completion rate"
                    loading={loading}
                    color="success"
                />
            </div>

            {/* Charts Row 1 */}
            <div className="charts-row">
                <div className="chart-container chart-wide">
                    <HiresByMonthChart filters={filters} />
                </div>
            </div>

            {/* Charts Row 2 */}
            <div className="charts-row">
                <div className="chart-container chart-half">
                    <HiresByJobTitleChart filters={filters} />
                </div>
                <div className="chart-container chart-half">
                    <HiresBySourceChart filters={filters} />
                </div>
            </div>

            {/* Recent Hires Table */}
            <div className="table-section">
                <RecentHiresTable filters={filters} />
            </div>
        </div>
    );
}

export default HiresDashboard;
